import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { readableTypeName } from '@iotv/core-services';
import { BranchingQueryDefinition, DeliveryType, edgeTypeMatrix, EntityRules, Group, MetricRule, MetricRuleValueNames, NotificationSubscription, Ruleset, SubscribedStatus, } from '@iotv/datamodel';
import MailIcon from '@mui/icons-material/Mail';
import PublicIcon from '@mui/icons-material/Public';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import SmsIcon from '@mui/icons-material/Sms';
import { Alert, alertClasses, Autocomplete, Stack, Tooltip, Typography } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import Assembler from '../../../data/Assembler';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { CustomerDAO } from '../../../data/SystemManagementDAO';
import { ActionIcons, getIconForType } from '../../../Iconography';
import { AdjacentType } from '../../../types';
import { AutosizedPopper } from '../../common/AutosizedPopper';
import { AutosizedTextField } from '../../common/AutosizedTextField';
import { ConfirmButton } from '../../common/ConfirmButton';
import { matching } from '../Utilities/CompareRulesets';
import { options } from './AlertManagement';
import { AlertManagementContext } from './AlertManagementContext';
const debug = process.env.REACT_APP_DEBUG && false;
const NotificationIcon = getIconForType('Notification');
var State;
(function (State) {
    State[State["GLOBAL_AND_LOCAL"] = 0] = "GLOBAL_AND_LOCAL";
    State[State["GLOBAL_ONLY"] = 1] = "GLOBAL_ONLY";
    State[State["LOCAL_ONLY"] = 2] = "LOCAL_ONLY";
    State[State["NONE"] = 3] = "NONE";
})(State || (State = {}));
/** A ruleset can get to all of type, or to specific groups
 * Subscriptions to a ruleset can get for all of type, or per group.
 * If the subscribing user is an admin, subscribe them to all of type using that ruleset (so in effect they only get subscribed to groups in the ruleset, but their subscription will update to match selected groups.
 * If the subscribing user is not an admin, they can only subscribe to groups they can see, and they will subscribe to all groups they can see. This will update the ruleset with those visible groups ? (Maybe it should be set by an admin and the user subscribes to the visible subset of those groups)
 * **/
export const GlobalRuleset = ({ contextCustomer: contextCustomerIn, user, userSelectedGroup, global, local }) => {
    const [context, dispatch] = useContext(AlertManagementContext);
    const { contextCustomer, contextUser, localRulesets, visibleGroups, contextUserIsAdmin } = context;
    const optionsContext = {
        contextCustomer: contextCustomerIn,
        user,
        userSelectedGroup,
        ...context,
        global,
        local,
    };
    const relevantVisibleGroups = local ? visibleGroups.filter((g) => g.supportedType === local.appliesToType) : [];
    const [fetchingSubscriptions, setFetchingSubscriptions] = useState(false);
    const [subscriptions, setSubscriptions] = useState([]);
    const deliveryTypes = new Set(subscriptions
        .map((s) => s.deliveryPreferences)
        .flat()
        .map((d) => d.deliveryType) ?? []);
    const [mismatched, setMismatched] = useState([]);
    const [processingRuleset, setProcessingRuleset] = useState(false);
    const [processingGroups, setProcessingGroups] = useState(false);
    const [processingSubscription, setProcessingSubscription] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [mismatchMessage, mismatchButton] = options.mismatchMessage(optionsContext, mismatched);
    const getSubscriptionTemplate = useCallback((ruleset) => {
        const zombie = {
            id: 'never',
            type: 'NotificationSubscription',
            deliveryPreferences: options
                .subscriptionDeliveryTypes(optionsContext)
                .map((t) => ({ deliveryType: t, recipients: [contextUser], destinations: [] })),
            filter: {
                severities: [],
                statuses: [],
                onActions: options.subscriptionFilters(optionsContext),
            },
            incidentEntityOrGroupingSk: 'never',
            incidentInitiatorSk: undefined,
            incidentProducingTypeId: ruleset.appliesToType ?? '', // WARN: this key updated for groups
            incidentType: EntityRules.BUSINESS_RULE_VIOLATION,
            incidentSubtype: ruleset.sk,
            subscriptionState: {
                status: SubscribedStatus.ACTIVE,
            },
        };
        return zombie;
    }, [options, contextUser]);
    useEffect(() => {
        if (global === undefined || local === undefined)
            return setMismatched([]);
        setMismatched(matching(global, local));
    }, [global, local]);
    // TODO: i think should be replaced with groups fetched in alert management and kept in context
    const [groups, setGroups] = useState([]);
    // Groups linked to LOCAL ruleset. Note that this will be none if the ruleset applies to all of type
    const [linkedGroups, setLinkedGroups] = useState([]);
    useEffect(() => {
        if (local)
            fetchSubscriptions();
    }, [local?.sk, contextUser?.sk]);
    useEffect(() => {
        if (local?.appliesToType) {
            if (!local.globallyApplyToObjectTypeId)
                fetchGroups();
        }
    }, [local?.appliesToType]);
    let state = State.NONE;
    if (global?.global) {
        if (local !== undefined)
            state = State.GLOBAL_AND_LOCAL;
        else
            state = State.GLOBAL_ONLY;
    }
    else {
        if (local !== undefined)
            state = State.LOCAL_ONLY;
        else
            state = State.NONE;
    }
    let icon = _jsx(PublicOffIcon, { color: "error", sx: { opacity: 0.6, fontSize: '2em' } });
    let name = '';
    let warning = undefined;
    let warningButton = _jsx(_Fragment, {});
    let button = _jsx(_Fragment, {});
    /** Creates a copy of the global ruleset and links it to current context customer, then subscribes */
    const add = async () => {
        if (processingRuleset || !contextCustomer || !global)
            return;
        setProcessingRuleset(true);
        debug && console.log('GlobalRuleset', 'adding...');
        const { pk, sk, id, ...rest } = global;
        const clone = new Ruleset(rest);
        debug && console.log('GlobalRuleset', clone);
        debug && console.log('GlobalRuleset', 'creating and linking...');
        const { err, data } = await ConsumerDao.createAndLink({
            existingObject: contextCustomer,
            newObject: clone,
            edgeTypeId: edgeTypeMatrix.Customer.Ruleset.customerHasRuleset.edgeTypeId,
        });
        debug && console.log('GlobalRuleset', err, data);
        if (err || !data)
            return setProcessingRuleset(false);
        const [source, edge, target] = data;
        debug && console.log('GlobalRuleset', 'cloning...');
        const local = new Ruleset(target);
        localRulesets.push(local);
        debug && console.log('GlobalRuleset', localRulesets);
        dispatch({ localRulesets: localRulesets.sort((a, b) => a?.name?.localeCompare(b?.name ?? '') ?? 0) });
        debug && console.log('GlobalRuleset', 'dispatched new local rulesets');
        setProcessingRuleset(false);
        await addSubscriptions(local);
    };
    // TODO: We should probably have another warning for if you can see more groups than you're subscribed to
    const addSubscriptions = async (ruleset) => {
        if (!contextUser || !contextCustomer)
            return;
        setProcessingSubscription(true);
        // Generate subscriptions that will be saved
        // If context user (from the dropdown, not viewer of the page) is an admin, it's one subscription to all entities of type in the ruleset
        // If the context user is not an admin, it's one subscription for each visible group
        let subscriptionPromises = [];
        const masterSubscriptionZombie = getSubscriptionTemplate(ruleset);
        const canAdmin_res = await CustomerDAO.canAdminCustomer({ contextUser: contextUser, customer: contextCustomer });
        debug && console.log('GlobalRuleset', 'add subscription context user can admin res', canAdmin_res);
        if (canAdmin_res.err || !canAdmin_res.data) {
            // Context user is not admin - one sub for each visible group of rulesets type
            let desiredSubscribeGroups = visibleGroups.filter((vg) => vg.supportedType === ruleset.appliesToType);
            debug && console.log('GlobalRuleset', 'subs after type filter', desiredSubscribeGroups);
            subscriptionPromises = desiredSubscribeGroups.map((vg) => new Promise(async (res, rej) => {
                const addSub_res = await addSubscriptionForGroup(vg, ruleset, contextUser);
                if (addSub_res.data) {
                    if (!local?.globallyApplyToObjectTypeId) {
                        linkGroupToRuleset(vg);
                    }
                    res(addSub_res.sub);
                }
                else {
                    rej({ err: addSub_res.err, group: vg });
                }
            }));
        }
        else {
            // Context user is admim
            subscriptionPromises = [
                new Promise(async (res, rej) => {
                    const subToLoadZombie = structuredClone(masterSubscriptionZombie);
                    subToLoadZombie.id = v4();
                    subToLoadZombie.incidentEntityOrGroupingSk = ruleset.appliesToType + ':*';
                    const sub = new NotificationSubscription(subToLoadZombie);
                    const { err, data } = await ConsumerDao.createAndLink({
                        existingObject: contextUser,
                        newObject: sub,
                        edgeTypeId: edgeTypeMatrix.User.NotificationSubscription.User_has_NotificationSubscription.edgeTypeId,
                    });
                    if (data) {
                        res(sub);
                    }
                    else {
                        rej({ err, group: null });
                    }
                }),
            ];
        }
        const subs_res = await Promise.allSettled(subscriptionPromises);
        const newSubs = subs_res.filter((s) => s.status === 'fulfilled').map((s) => s.value);
        if (newSubs.length > 0) {
            debug &&
                console.log('GlobalRuleset', 'successfully subscribed to groups', newSubs.map((s) => s.incidentEntityOrGroupingSk));
            setSubscriptions((old) => [...old, ...newSubs]);
        }
        const failedGroupReses = subs_res.filter((s) => s.status === 'rejected' && !!s.reason.group);
        const failedGroups = failedGroupReses.map((s) => s.status === 'rejected' && s.reason.group); // Types wouldn't be quiet so I have to check for rejected again
        if (failedGroups.length > 0) {
            // Idk... just a user message?
        }
        setProcessingSubscription(false);
    };
    // Contructs and saves a subscription for a particular group
    // NOTE: doesn't update state or anything, that's left to the caller
    const addSubscriptionForGroup = async (group, ruleset, user) => {
        const subZombie = getSubscriptionTemplate(ruleset);
        subZombie.id = v4();
        subZombie.incidentEntityOrGroupingSk = group.sk;
        subZombie.incidentProducingTypeId = 'Group';
        subZombie.incidentEntityOrGroupingName = group.name;
        const sub = new NotificationSubscription(subZombie);
        const { err, data } = await ConsumerDao.createAndLink({
            existingObject: user,
            newObject: sub,
            edgeTypeId: edgeTypeMatrix.User.NotificationSubscription.User_has_NotificationSubscription.edgeTypeId,
        });
        return { err, data, sub };
    };
    const removeSubscriptionForGroup = async (group) => {
        if (!contextUser)
            return;
        const sub = subscriptions.find((s) => s.incidentEntityOrGroupingSk === group.sk);
        if (!sub) {
            console.warn('GlobalRuleset', `attempted to delete subscription for group ${group.sk}, but no subscription was found`);
            return;
        }
        await ConsumerDao.unlink(contextUser, sub);
        await ConsumerDao.deleteExact(sub);
        setSubscriptions((old) => [...old.filter((s) => s.sk !== sub.sk)]);
    };
    /** Removes the copy of the global ruleset (and any subscriptions) from the context customer */
    // TODO:
    const remove = async () => {
        if (processingRuleset || !contextCustomer || !local)
            return;
        await removeSubscriptions();
        setProcessingRuleset(true);
        debug && console.log('GlobalRuleset', 'removing...');
        const results = [];
        const unlinking = linkedGroups.map(async (g) => {
            results.push(await ConsumerDao.unlink(local, g));
        });
        await Promise.all(unlinking);
        let err = results.filter((r) => r.err !== null).shift();
        if (err)
            return setProcessingRuleset(false);
        setLinkedGroups([]);
        results.push(await ConsumerDao.unlink(contextCustomer, local));
        results.push(await ConsumerDao.deleteExact(local));
        err = results.filter((r) => r.err !== null).shift();
        debug && console.log('GlobalRuleset', err);
        if (err)
            return setProcessingRuleset(false);
        debug && console.log('GlobalRuleset', 'cloning...');
        const clone = localRulesets.filter((r) => r.name !== local.name).sort((a, b) => a?.name?.localeCompare(b?.name ?? '') ?? 0);
        debug && console.log('GlobalRuleset', clone);
        dispatch({ localRulesets: clone });
        debug && console.log('GlobalRuleset', 'dispatched new local rulesets');
        setProcessingRuleset(false);
    };
    const removeSubscriptions = async () => {
        if (!contextUser || subscriptions.length === 0)
            return;
        setProcessingSubscription(true);
        const { err, data } = await ConsumerDao.batchDelete({ Items: subscriptions });
        debug && console.log('GlobalRuleset', err, data);
        if (err || !data)
            return;
        setSubscriptions([]);
        //unlinkAllGroupsFromRuleset()
        setProcessingSubscription(false);
    };
    /** Updates local ruleset to match the global ruleset (in case they differ) */
    const update = async () => {
        if (processingRuleset || !contextCustomer || !global || !local)
            return;
        setUpdating(true);
        debug && console.log('GlobalRuleset', 'updating...');
        await removeSubscriptions();
        setProcessingRuleset(true);
        const { pk, sk, id, ...rest } = global;
        const clone = new Ruleset({ ...local, ...rest });
        debug && console.log('GlobalRuleset', global.id, local.id, clone.id, clone);
        const { err, data } = await ConsumerDao.save(clone);
        if (err || !data) {
            setProcessingRuleset(false);
            setUpdating(false);
            return;
        }
        const i = localRulesets.findIndex((r) => r.name === local.name);
        if (i === -1) {
            setProcessingRuleset(false);
            setUpdating(false);
            return;
        }
        const updated = new Ruleset(data);
        localRulesets.splice(i, 1, updated);
        dispatch({ localRulesets: localRulesets.sort((a, b) => a?.name?.localeCompare(b?.name ?? '') ?? 0) });
        debug && console.log('GlobalRuleset', 'dispatched new local rulesets');
        setProcessingRuleset(false);
        await addSubscriptions(updated);
        setUpdating(false);
    };
    const fetchSubscriptions = async () => {
        setFetchingSubscriptions(true);
        setSubscriptions([]);
        if (local === undefined || contextUser === undefined)
            return;
        const { err, data } = await ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [contextUser],
            query: new BranchingQueryDefinition({
                id: contextUser.sk,
                path: [{ objectTypeId: 'NotificationSubscription', adjacencyType: AdjacentType.CHILD }],
                subQueries: [],
            }),
        });
        if (err || !data) {
            setFetchingSubscriptions(false);
            return;
        }
        const subscriptions = data.getNodesOfType('NotificationSubscription').map((vob) => new NotificationSubscription(vob));
        debug && console.log('GlobalRuleset', 'fetched', subscriptions.length, 'subscriptions');
        const found = subscriptions.filter((s) => s.incidentSubtype === local.sk);
        debug && console.log('GlobalRuleset', 'found subscriptions for', new Set(found.map((f) => f?.incidentSubtype)));
        setSubscriptions(found);
        setFetchingSubscriptions(false);
    };
    /** Gets group children from context customer (for selection (possible unneeded now we have visible groups)) + groups linked to the ruleset */
    const fetchGroups = async () => {
        if (local?.appliesToType === undefined || contextCustomer === undefined)
            return;
        setProcessingGroups(true);
        // fetch all groups for the entity type
        let items = [];
        // TODO: should be gatehred by alert management page and passed here
        const allGroups = await ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [contextCustomer],
            query: new BranchingQueryDefinition({ id: contextCustomer.sk, path: [{ objectTypeId: 'Group', adjacencyType: AdjacentType.CHILD }], subQueries: [] }),
        });
        if (allGroups.err)
            return setProcessingGroups(false);
        items =
            allGroups.data
                ?.getNodesOfType('Group')
                ?.filter((group) => group && group.supportedType === local.appliesToType)
                .map((group) => Assembler.getInstance(group))
                .filter((group) => group !== undefined) ?? [];
        setGroups(items);
        // fetch linked groups
        const { err, data } = await ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [local],
            query: new BranchingQueryDefinition({ id: local.sk, path: [{ objectTypeId: 'Group', adjacencyType: AdjacentType.CHILD }] }),
        });
        if (err)
            return setProcessingGroups(false);
        if (data)
            setLinkedGroups(data.getNodesOfType('Group').map((vob) => new Group(vob)));
        setProcessingGroups(false);
    };
    // Called when manipulating the autocomplete
    const selectGroup = (group) => {
        // Link Group (if ruleset requires group to be linked)
        if (local && !local.globallyApplyToObjectTypeId) {
            linkGroupToRuleset(group);
        }
        // Subscribe to group (unless subscription is to all of type)
        if (!contextUserIsAdmin && contextUser && local) {
            addSubscriptionForGroup(group, local, contextUser).then((sub_res) => {
                if (sub_res.sub) {
                    setSubscriptions((old) => [...old, sub_res.sub]);
                }
            });
        }
    };
    const deselectGroup = (group) => {
        // Unlink Group (if ruleset requires groups to be linked)
        if (local && !local.globallyApplyToObjectTypeId) {
            unlinkGroupFromRuleset(group);
        }
        // Unsubscribe to group (unless subscription is to all of type, inferred here by if the context user is an admin)
        if (!contextUserIsAdmin && contextUser && local) {
            removeSubscriptionForGroup(group);
        }
    };
    /** Links the local ruleset to a selected group */
    const linkGroupToRuleset = async (group) => {
        if (processingRuleset || processingSubscription || !local)
            return;
        const { err, data } = await ConsumerDao.link(local, group, edgeTypeMatrix.Ruleset.Group.ruleset_has_group.edgeTypeId);
        if (err || !data)
            return setProcessingGroups(false);
        const [source, edge, target] = data;
        if (!target)
            setProcessingGroups(false);
        if (linkedGroups.find((lg) => lg.sk === group.sk) === undefined) {
            setLinkedGroups((old) => [...old, group]);
        }
        setProcessingGroups(false);
    };
    /** Unlinks a group from a ruleset, but does not delete subscriptions for that group attached to that ruleset
     * WARN: admin only **/
    const unlinkGroupFromRuleset = async (group) => {
        if (processingRuleset || !local)
            return;
        setProcessingGroups(true);
        if (!group)
            return setProcessingGroups(false);
        const { err, data } = await ConsumerDao.unlink(local, group);
        if (err)
            return setProcessingGroups(false);
        setLinkedGroups((old) => [...old.filter((og) => og.sk !== group.sk)]);
        setProcessingGroups(false);
    };
    const unlinkAllGroupsFromRuleset = async () => {
        if (processingRuleset || !local)
            return;
        setProcessingGroups(true);
        const results = await Promise.all(linkedGroups.map(async (g) => await ConsumerDao.unlink(local, g)));
        const err = results.filter((r) => r.err !== null).shift();
        if (err)
            setProcessingGroups(false);
        setLinkedGroups([]);
        setProcessingGroups(false);
        setProcessingSubscription(true);
        const sub_results = await Promise.all(linkedGroups.map(async (g) => await removeSubscriptionForGroup(g)));
        setProcessingSubscription(false);
    };
    /** Assemble components based on state */
    switch (state) {
        case State.GLOBAL_AND_LOCAL:
            if (global?.global && local?.global)
                icon = _jsx(PublicIcon, { color: "primary", sx: { fontSize: '2em' } });
            if (global?.global && !local?.global)
                icon = _jsx(PublicIcon, { color: "warning", sx: { fontSize: '2em' } });
            name = global?.name ?? '';
            if (global?.id !== local?.id)
                button = (_jsx(ConfirmButton, { variant: "outlined", color: "error", startIcon: _jsx(ActionIcons.Delete, {}), confirmMessage: "Remove?", onConfirm: remove, loading: processingRuleset && !updating, disabled: processingRuleset || processingGroups || processingSubscription, sx: { width: '8em' }, children: "Remove" }));
            break;
        case State.GLOBAL_ONLY:
            icon = _jsx(PublicIcon, { color: "disabled", sx: { fontSize: '2em' } });
            name = global?.name ?? '';
            button = (_jsx(ConfirmButton, { variant: "outlined", color: "success", startIcon: _jsx(ActionIcons.Create, {}), confirmMessage: "Add?", onConfirm: add, loading: processingRuleset && !updating, disabled: processingRuleset || processingGroups || processingSubscription, sx: { width: '8em' }, children: "Add" }));
            break;
        case State.LOCAL_ONLY:
            icon = _jsx(PublicOffIcon, { color: "warning", sx: { fontSize: '2em' } });
            name = local?.name ?? '';
            warning = 'No global ruleset found!';
            warningButton = (_jsx(ConfirmButton, { color: "inherit", startIcon: _jsx(ActionIcons.Delete, {}), confirmMessage: "Remove?", onConfirm: remove, loading: processingRuleset && !updating, disabled: processingRuleset || processingGroups || processingSubscription, children: "Remove" }));
            break;
        case State.NONE:
            icon = _jsx(PublicOffIcon, { color: "error", sx: { opacity: 0.6, fontSize: '2em' } });
            warning = 'No ruleset specified!';
            break;
    }
    const components = {
        icon,
        button,
        name: _jsx(Typography, { variant: "h6", children: name }),
        warning: warning && (_jsx(Stack, { children: _jsx(Alert, { severity: "warning", action: warningButton, sx: { paddingBlock: 0, paddingInlineEnd: 1, alignItems: 'center', [`& .${alertClasses.action}`]: { paddingBlock: 0 } }, children: warning }) })),
        affects: local && !local.globallyApplyToObjectTypeId && (_jsx(Autocomplete, { value: linkedGroups, freeSolo: false, multiple: true, options: (contextUserIsAdmin ? groups : relevantVisibleGroups).sort((a, b) => a?.name?.localeCompare(b?.name ?? '') ?? 0), getOptionLabel: (option) => option?.name ?? option.sk, onChange: (_, __, reason, details) => {
                switch (reason) {
                    case 'selectOption':
                        if (!details?.option)
                            return;
                        return selectGroup(details.option);
                    case 'removeOption':
                        if (!details?.option)
                            return;
                        return deselectGroup(details.option);
                    case 'clear':
                        return unlinkAllGroupsFromRuleset();
                }
            }, loading: processingGroups, disabled: processingRuleset || processingGroups || processingSubscription, size: "small", slots: { popper: AutosizedPopper }, renderInput: (params) => (_jsx(AutosizedTextField, { ...params, label: `${readableTypeName(local.appliesToType)} groups`, error: !processingRuleset && linkedGroups.length === 0 })) })),
        subscription: local &&
            (subscriptions.length > 0 ? (_jsxs(_Fragment, { children: [deliveryTypes.has(DeliveryType.EMAIL) ? (_jsx(Tooltip, { title: "Email notification", arrow: true, children: _jsx(MailIcon, {}) })) : (_jsx(_Fragment, {})), deliveryTypes.has(DeliveryType.MOBILE_PUSH) ? (_jsx(Tooltip, { title: "Push notification", arrow: true, children: _jsx(SmartphoneIcon, {}) })) : (_jsx(_Fragment, {})), deliveryTypes.has(DeliveryType.SMS) ? (_jsx(Tooltip, { title: "SMS notification", arrow: true, children: _jsx(SmsIcon, {}) })) : (_jsx(_Fragment, {})), _jsx(ConfirmButton, { color: "error", startIcon: _jsx(NotificationIcon, {}), confirmMessage: "Unsubscribe?", onConfirm: () => removeSubscriptions(), loading: processingSubscription, disabled: processingRuleset || processingGroups || processingSubscription, children: "Unsubscribe" })] })) : (_jsx(ConfirmButton, { color: "success", startIcon: _jsx(NotificationIcon, {}), confirmMessage: "Subscribe?", onConfirm: () => addSubscriptions(local), loading: processingSubscription, disabled: processingRuleset || processingGroups || processingSubscription, children: "Subscribe" }))),
        thresholds: local &&
            local.definition.MetricRule?.map((r) => {
                const rule = new MetricRule(r);
                const key = rule.key;
                const trigger = typeof key === 'object' ? key.name : key;
                const max = r.max.value !== null && (_jsx(AutosizedTextField, { label: "Max", placeholder: String(rule.max.value), value: r.max.value, disabled: processingRuleset }));
                const min = r.min.value !== null && (_jsx(AutosizedTextField, { label: "Min", placeholder: String(rule.min.value), value: r.min.value, disabled: processingRuleset }));
                const std = r.stdDev.value !== null && (_jsx(AutosizedTextField, { label: "Std.", placeholder: String(rule.stdDev.value), value: r.stdDev.value, disabled: processingRuleset }));
                return (_jsxs(_Fragment, { children: [_jsxs(Typography, { children: [r.name, " (", MetricRuleValueNames[r.valueType], " ", trigger, ")"] }), max, min, std] }));
            }),
        alert: 0 < mismatched.length && (_jsx(Stack, { justifyContent: 'center', children: _jsx(Alert, { severity: "warning", action: _jsx(ConfirmButton, { color: "inherit", confirmMessage: "Update?", onConfirm: update, loading: updating, disabled: processingRuleset, children: mismatchButton }), sx: { paddingBlock: 0, paddingInlineEnd: 1, alignItems: 'center', [`& .${alertClasses.action}`]: { paddingBlock: 0 } }, children: mismatchMessage }) })),
        groupWarning: local &&
            local.globallyApplyToObjectTypeId &&
            !fetchingSubscriptions &&
            !processingGroups &&
            0 < subscriptions.length &&
            (subscriptions.find((s) => !relevantVisibleGroups.find((vg) => s.incidentEntityOrGroupingSk === vg.sk)) ? (_jsx(Alert, { severity: "warning", children: "You are subscribed to some groups you cannot see" })) : relevantVisibleGroups.find((vg) => !subscriptions.find((s) => s.incidentEntityOrGroupingSk === vg.sk)) ? (_jsx(Alert, { severity: "info", children: "Some groups are visible but you aren't subscribed" })) : null),
    };
    return (_jsxs(Stack, { padding: 1, direction: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 1, sx: { borderRadius: 1, border: (t) => `1px solid ${t.palette.divider}` }, children: [components.icon, components.button, components.name, components.warning, components.affects, components.subscription, components.alert, components.groupWarning] }));
};
